import React from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { Chip } from 'src/components/shared/Chip'
import type { InternalAccessUser } from 'src/interfaces/internalAccess'
import { formatDateTime, parseFlexibleDateTime } from 'src/utils/grid'
import { buildInternalUserName } from 'src/utils/internalAccess/internalUsers'
import { InternalUserStatusChip } from './InternalUserStatusChip'

/**
 * Every row is loaded (no server pagination), so client sorting is correct and left on.
 * `lastActiveAt`/`disabledAt` are ISO strings and `createdAt` unix seconds —
 * `parseFlexibleDateTime` takes both and degrades to an em dash.
 */
export const buildInternalUserColumns = (
  currentUserId: string | null,
): Array<GridColDef<InternalAccessUser>> => {
  return [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 200,
      hideable: false,
      display: 'flex',
      valueGetter: (value: unknown, row: InternalAccessUser) => {
        return buildInternalUserName(row)
      },
      renderCell: (params: GridRenderCellParams<InternalAccessUser>) => {
        return (
          <div css={tw`flex min-w-0 items-center gap-2`}>
            <Typography variant="body2" css={tw`truncate`}>
              {buildInternalUserName(params.row)}
            </Typography>
            {params.row.id === currentUserId ? (
              <Chip text="You" dataTestId="internal-user-you-chip" />
            ) : null}
          </div>
        )
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 2,
      minWidth: 220,
    },
    {
      field: 'roleName',
      headerName: 'Role',
      flex: 1,
      minWidth: 140,
      display: 'flex',
      valueGetter: (value: string | null) => {
        return value ?? ''
      },
      renderCell: (params: GridRenderCellParams<InternalAccessUser>) => {
        if (!params.row.roleName) {
          return (
            <Chip
              text="No role"
              className={tw`bg-shades-100`}
              textClassName={tw`text-shades-500`}
              dataTestId="internal-user-no-role"
            />
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.roleName}
          </Typography>
        )
      },
    },
    {
      field: 'disabled',
      headerName: 'Status',
      width: 120,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InternalAccessUser>) => {
        return <InternalUserStatusChip isDisabled={params.row.disabled} />
      },
    },
    {
      field: 'lastActiveAt',
      headerName: 'Last active',
      flex: 1,
      minWidth: 180,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 1,
      minWidth: 180,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
  ]
}
